import React, { useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import TopBar from '../layout/TopBar';
import StatusBadge from '../components/StatusBadge';
import SchemaQuestionCard from '../components/SchemaQuestionCard';
import FilterBanner from '../components/FilterBanner';
import { useHGS } from '../context/HGSContext';
import { MODULE_SCHEMAS } from '../data/masterData';
import { computeResidualScore, bandFor, FRAUD_TRIGGER_IDS, PCC_MANDATORY_IDS } from '../data/riskUniverse';
import { ArchitectureProgress } from './RiskUniversePage';
import { filterRowsForUser, canFillModuleForms } from '../data/rbac';
import RiskUnpublishedGate from '../components/RiskUnpublishedGate';
import MasterEmptyState from '../components/MasterEmptyState';
import { ChevronDown, ChevronRight, Filter, Code2, Sparkles, Lock, AlertTriangle, ArrowRight, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';

const ANSWERABLE = ['responseType', 'evidenceFile', 'currentSituation', 'countermeasure', 'targetCompletionDate'];

export default function PCCPage() {
  const { masters, masterMeta, formResponses, riskUniverse, user, riskPublished } = useHGS();
  const [searchParams] = useSearchParams();
  const filter = searchParams.get('filter');
  const schema = MODULE_SCHEMAS.pcc;
  const meta = masterMeta.pcc;
  const allRows = useMemo(() => filterRowsForUser(user, (masters.pcc || []).filter((r) => r.active !== false)), [masters, user]);
  const canEdit = canFillModuleForms(user);
  const masterUploaded = (masters.pcc || []).length > 0;

  // Gate: PCC runs after Fraud. Fraud is triggered by financial risks R23–R27, R30 at Med/High.
  const fraudTriggered = useMemo(() =>
    (riskUniverse || []).some((r) => {
      if (!FRAUD_TRIGGER_IDS.includes(r.id)) return false;
      const { residual } = computeResidualScore(r);
      const b = bandFor(residual);
      return b === 'Medium' || b === 'High';
    })
  , [riskUniverse]);

  // Has any Fraud response been submitted (signal that user has moved through Fraud step)
  const fraudAnswered = useMemo(() =>
    Object.keys(formResponses || {}).some((k) => k.startsWith('fraud:'))
  , [formResponses]);

  // Mandatory PCC risks currently Medium/High
  const mandatoryRisks = useMemo(() => {
    return (riskUniverse || [])
      .filter((r) => PCC_MANDATORY_IDS.includes(r.id))
      .map((r) => {
        const { inherent, residual } = computeResidualScore(r);
        return { ...r, inherent, residual, band: bandFor(residual) };
      });
  }, [riskUniverse]);

  // B4 Subcontractor response → if "No" flag risk R20
  const b4Response = useMemo(() => {
    // Find a pcc response whose sectionName / questionId indicates B4 with "No"
    return Object.values(formResponses || {})
      .find((resp) => resp.moduleKey === 'pcc' && /B4|subcontractor/i.test(resp.values?.sectionName || resp.recordId || '') && resp.values?.responseType === 'No');
  }, [formResponses]);

  const rows = useMemo(() => {
    if (!filter) return allRows;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return allRows.filter((r) => {
      const resp = formResponses[`pcc:${r.questionId}`];
      const v = resp?.values || {};
      switch (filter) {
        case 'missing-evidence':
          return resp && v.responseType && !v.evidenceFile;
        case 'pending':
          return !resp || !v.responseType;
        case 'no-or-na':
          return v.responseType === 'No' || v.responseType === 'N/A';
        case 'overdue': {
          if (!(v.responseType === 'No' || v.responseType === 'N/A')) return false;
          if (!v.targetCompletionDate) return false;
          const due = new Date(v.targetCompletionDate);
          return !Number.isNaN(due.getTime()) && due < today;
        }
        default:
          return true;
      }
    });
  }, [allRows, filter, formResponses]);

  // Group by sectionName (master classification field)
  const groups = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const key = r.sectionName || 'General';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries());
  }, [rows]);

  const [open, setOpen] = useState({});
  // Auto-expand all sections when a filter is active so the user sees all hits
  const sectionsOpen = useMemo(() => {
    if (filter) {
      const all = {};
      groups.forEach(([name]) => { all[name] = true; });
      return all;
    }
    if (groups[0] && Object.keys(open).length === 0) return { [groups[0][0]]: true };
    return open;
  }, [filter, groups, open]);

  return (
    <div>
      <TopBar
        title="Product Compliance Checklist (PCC)"
        subtitle="Step 3 of 5 · Triggered by Fraud · mandatory for PCC-risk flags (R07, R08, R19–R21, R31)"
        breadcrumbs={['HGS Architecture', 'PCC']}
        actions={
          <button className="hidden md:inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-lg transition">
            <Filter className="w-4 h-4" />
            Filter
          </button>
        }
      />
      <div className="p-8 space-y-4">
        <ArchitectureProgress step={3} />

        {!riskPublished?.published && <RiskUnpublishedGate user={user} />}

        {riskPublished?.published && !canEdit && (
          <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200" data-testid="review-mode-banner">
            <Lock className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
            <div className="text-[11px] text-slate-600 leading-relaxed">
              <span className="font-bold text-slate-900">Review-only mode</span> — PCC is filled by Department Heads. You can view their responses; approve / reject from Approvals.
            </div>
          </div>
        )}

        {riskPublished?.published && !masterUploaded && (
          <MasterEmptyState moduleLabel="Product Compliance (PCC)" moduleKey="pcc" accent="#1F2937" isAdmin={user?.role === 'admin'} />
        )}

        {riskPublished?.published && masterUploaded && (<>
        {!fraudTriggered && !fraudAnswered && (
          <div className="bg-white rounded-xl border border-amber-200 p-6 flex items-start gap-4" data-testid="pcc-gate-fraud-not-done">
            <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-bold text-slate-900">PCC becomes applicable after Fraud</h3>
              <p className="text-sm text-slate-600 mt-1">
                The PCC checklist runs <b>after</b> the Fraud Prevention step in the HGS Architecture. Complete the Fraud checklist or confirm no financial risks are at Medium/High.
              </p>
              <Link to="/fraud" className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#CC0000] hover:underline">
                Go to Fraud Prevention → <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        )}

        {/* Mandatory PCC risk strip */}
        {mandatoryRisks.length > 0 && (
          <div className="bg-white rounded-xl border border-violet-200 p-5" data-testid="pcc-mandatory-strip">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center">
                <ShieldAlert className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">PCC mandatory for {mandatoryRisks.length} risks</h3>
                <p className="text-[11px] text-slate-500">These risks require every PCC section to be answered before DLC sign-off.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {mandatoryRisks.map((r) => (
                <span key={r.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-violet-50 border border-violet-200 text-[11px] font-bold text-violet-900">
                  <span className="font-mono">{r.id}</span>
                  <span className="font-normal truncate max-w-[200px]">{r.description}</span>
                  <span className={cn('px-1.5 py-0.5 rounded text-[9px] uppercase font-bold',
                    r.band === 'High' ? 'bg-red-600 text-white' :
                    r.band === 'Medium' ? 'bg-amber-400 text-amber-950' :
                    'bg-slate-200 text-slate-700')}>
                    {r.band}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* B4 = No → R20 exposure auto-increase */}
        {b4Response && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3" data-testid="b4-no-alert">
            <AlertTriangle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-bold text-red-900">B4 answered No — R20 exposure increased</div>
              <p className="text-xs text-red-800 mt-1">
                Because a B4 (subcontractor) question was answered "No", the residual risk exposure for <span className="font-mono font-bold">R20 · Alternate-material approval gaps</span> has been automatically uplifted. A countermeasure task has been created.
              </p>
              <Link to="/countermeasures" className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-red-900 hover:underline">
                View countermeasures → <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        )}

        {filter && (
          <FilterBanner activeFilter={filter} count={rows.length} totalCount={allRows.length} />
        )}

        {/* Schema source banner */}
        <div className="flex items-start gap-3 p-4 rounded-xl bg-gradient-to-r from-slate-900 to-[#1a0000] text-white">
          <Sparkles className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold flex items-center gap-2 flex-wrap">
              Forms below are rendered live from
              <span className="font-mono px-2 py-0.5 rounded bg-white/10 text-amber-300">{schema.label}</span>
              <span className="font-mono px-2 py-0.5 rounded bg-white/10">{meta?.version || 'v1.0'}</span>
              <StatusBadge status={meta?.status || 'Active'} dot={false} />
            </div>
            <div className="text-[11px] text-slate-300 mt-1">
              {rows.length} active questions · {groups.length} sections · last published by {meta?.lastUploadedBy || '—'} on {meta?.lastUploadedAt || '—'}
            </div>
          </div>
          <Code2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
        </div>

        {/* Summary bar — live-computed against current PCC responses */}
        <PccLiveKpis allRows={allRows} formResponses={formResponses} groups={groups} />

        {filter && rows.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Filter className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-700">No questions match this filter</p>
            <p className="text-xs text-slate-500 mt-1">Try clearing the filter to see all PCC questions.</p>
          </div>
        )}

        {groups.map(([sectionName, sectionRows]) => {
          const isOpen = !!sectionsOpen[sectionName];
          return (
            <div key={sectionName} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <button
                onClick={() => setOpen((o) => ({ ...o, [sectionName]: !o[sectionName] }))}
                className="w-full p-5 flex items-center justify-between hover:bg-slate-50/60 transition"
              >
                <div className="flex items-center gap-3">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="text-base font-bold text-slate-900">Section: {sectionName}</span>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{sectionRows.length} questions</span>
                    </div>
                    <div className="text-xs text-slate-500">Each question rendered from the JSON schema below</div>
                  </div>
                </div>
              </button>
              {isOpen && (
                <div className="p-5 pt-0 space-y-3 border-t border-slate-100">
                  {sectionRows.map((row, i) => (
                    <SchemaQuestionCard
                      key={row[schema.idField]}
                      schema={schema}
                      questionRow={row}
                      index={i}
                      questionTextKey="complianceRequirement"
                      answerableKeys={ANSWERABLE}
                      readOnly={!canEdit}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
        </>)}
      </div>
    </div>
  );
}

function KPI({ label, value, tone = 'slate', testId, onClick }) {
  const colors = {
    slate: 'text-slate-900',
    emerald: 'text-emerald-600',
    red: 'text-[#CC0000]',
    amber: 'text-amber-600',
    violet: 'text-violet-700',
  };
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      data-testid={testId}
      className={cn('text-left', onClick && 'hover:bg-slate-50 rounded-lg -m-1 p-1 transition cursor-pointer')}
    >
      <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
      <div className={cn('text-2xl font-bold tabular-nums', colors[tone])}>{value}</div>
    </Tag>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Live KPI bar — computes the 4 spec trackers from current PCC responses:
//   • non-compliance (Yes/No/N/A counted) — number of No + N/A
//   • missing evidence — answered but evidence file blank
//   • overdue remediation — No/N/A with targetCompletionDate <= today
//   • open actions — countermeasures in the queue
// Plus a section/question total row.
// ──────────────────────────────────────────────────────────────────────────
function PccLiveKpis({ allRows, formResponses, groups }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFilter = searchParams.get('filter');

  const metrics = useMemo(() => {
    let answered = 0;
    let nonCompliant = 0;
    let missingEvidence = 0;
    let overdue = 0;
    const today = new Date(); today.setHours(0, 0, 0, 0);

    allRows.forEach((r) => {
      const resp = formResponses?.[`pcc:${r.questionId}`];
      const v = resp?.values || {};
      if (resp && v.responseType) answered += 1;
      if (v.responseType === 'No' || v.responseType === 'N/A') nonCompliant += 1;
      if (resp && v.responseType && !v.evidenceFile) missingEvidence += 1;
      if ((v.responseType === 'No' || v.responseType === 'N/A') && v.targetCompletionDate) {
        const due = new Date(v.targetCompletionDate);
        if (!Number.isNaN(due.getTime()) && due < today) overdue += 1;
      }
    });
    const completion = allRows.length === 0 ? 0 : Math.round((answered / allRows.length) * 100);
    return { answered, nonCompliant, missingEvidence, overdue, completion };
  }, [allRows, formResponses]);

  const setFilter = (key) => {
    const next = new URLSearchParams(searchParams);
    if (activeFilter === key) next.delete('filter');
    else next.set('filter', key);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="pcc-live-kpis">
      <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPI label="Sections" value={groups.length} testId="pcc-kpi-sections" />
        <KPI label="Questions" value={allRows.length} testId="pcc-kpi-questions" />
        <KPI label="Completion" value={`${metrics.completion}%`} tone={metrics.completion >= 75 ? 'emerald' : metrics.completion >= 40 ? 'amber' : 'red'} testId="pcc-kpi-completion" />
        <KPI label="Non-Compliance" value={metrics.nonCompliant} tone="red" testId="pcc-kpi-noncompliance"
          onClick={() => setFilter('no-or-na')} />
        <KPI label="Missing Evidence" value={metrics.missingEvidence} tone="violet" testId="pcc-kpi-missing-evidence"
          onClick={() => setFilter('missing-evidence')} />
        <KPI label="Overdue Remediation" value={metrics.overdue} tone="amber" testId="pcc-kpi-overdue"
          onClick={() => setFilter('overdue')} />
      </div>
      <div className="px-4 pb-3 flex items-center gap-1.5 flex-wrap border-t border-slate-100 pt-3">
        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mr-1">Filter:</span>
        {[
          { k: '', l: 'All' },
          { k: 'pending', l: 'Pending' },
          { k: 'no-or-na', l: 'Non-compliance' },
          { k: 'missing-evidence', l: 'Missing evidence' },
          { k: 'overdue', l: 'Overdue' },
        ].map((f) => (
          <button
            key={f.k || 'all'}
            data-testid={`pcc-filter-${f.k || 'all'}`}
            onClick={() => setFilter(f.k)}
            className={cn(
              'px-2.5 py-1 rounded-md text-[10px] font-bold transition',
              (activeFilter || '') === f.k
                ? 'bg-[#CC0000] text-white'
                : 'bg-slate-50 text-slate-600 hover:bg-slate-100',
            )}
          >
            {f.l}
          </button>
        ))}
      </div>
    </div>
  );
}
